import type { Feature, Field } from "../types";
const person: Field[] = [
  { key: "firstName", label: "First name", required: true },
  { key: "lastName", label: "Last name", required: true },
];
const department: Field = {
  key: "departmentId",
  label: "Department",
  type: "department",
  required: true,
};
const employee: Field = {
  key: "employeeId",
  label: "Host / recipient",
  type: "employee",
  required: true,
};
export const features: Feature[] = [
  {
    key: "visits",
    title: "Visitors",
    singular: "visitor",
    description: "A warm welcome starts with an organized front desk.",
    roles: ["Admin", "RH", "Assistente"],
    employeeVisible: true,
    defaultSort: "date",
    attachment: true,
    writeRoles: ["Admin", "Assistente"],
    fields: [
      ...person,
      { key: "email", label: "Email address", type: "email" },
      { key: "phone", label: "Phone number", type: "tel" },
      { key: "address", label: "Company / address" },
      {
        key: "reason",
        label: "Purpose of visit",
        type: "select",
        options: [
          "Consultant",
          "Reunion",
          "Fournisseur",
          "Visite",
          "Maintenance",
        ],
        required: true,
      },
      { key: "departmentIds", label: "Departments", type: "visitDepartments", required: true },
      { key: "employeeIds", label: "Hosts / recipients", type: "visitRecipients", required: true },
      { key: "date", label: "Visit date", type: "date", required: true },
      { key: "timeIn", label: "Check-in time", type: "time", required: true },
      { key: "timeOut", label: "Check-out time", type: "time" },
    ],
    columns: [
      {
        key: "firstName",
        label: "First name",
      },
      {
        key: "lastName",
        label: "Last name",
      },
      {
        key: "email",
        label: "Email",
      },
      {
        key: "phone",
        label: "Phone number",
      },
      {
        key: "address",
        label: "Company / address",
      },
      {
        key: "reason",
        label: "Purpose",
      },
      {
        key: "date",
        label: "Date",
      },
      {
        key: "timeIn",
        label: "Check-in",
      },
      {
        key: "timeOut",
        label: "Check-out time",
      },
      {
        key: "departmentIds",
        label: "Departments",
      },
      {
        key: "employeeIds",
        label: "Hosts / recipients",
      },
    ],
  },
  {
    key: "correspondence",
    title: "Correspondence",
    singular: "correspondence",
    description: "Every letter, document and delivery, accounted for.",
    roles: ["Admin", "Assistente", "Direction"],
    employeeVisible: true,
    defaultSort: "receivedDate",
    attachment: true,
    writeRoles: ["Admin", "Assistente"],
    fields: [
      {
        key: "type",
        label: "Document type",
        type: "select",
        options: [
          "Facture",
          "Courrier",
          "Livraison",
          "Lettre Recommandée",
          "Accusé de Réception",
        ],
        required: true,
      },
      { key: "company", label: "Sender / company", required: true },
      { key: "invoiceNumber", label: "Invoice number" },
      {
        key: "assistant",
        label: "Handled by",
        type: "directionUser",
      },
      department,
      employee,
      {
        key: "receivedDate",
        label: "Received date",
        type: "date",
        required: true,
      },
      {
        key: "deliveredDate",
        label: "Delivered date",
        type: "date",
        required: true,
      },
      {
        key: "status",
        label: "Status",
        type: "select",
        options: ["Arriver", "Depart"],
        required: true,
      },
    ],
    columns: [
      {
        key: "reference",
        label: "Reference",
      },
      {
        key: "company",
        label: "Sender",
      },
      {
        key: "invoiceNumber",
        label: "Invoice number",
      },
      {
        key: "type",
        label: "Type",
      },
      {
        key: "receivedDate",
        label: "Received date",
      },
      {
        key: "deliveredDate",
        label: "Delivered date",
      },
      {
        key: "status",
        label: "Status",
      },
      {
        key: "departmentId",
        label: "Department",
      },
      {
        key: "employeeId",
        label: "Recipient",
      },
    ],
  },
  {
    key: "interviews",
    title: "Interviews",
    singular: "interview",
    description: "Make space for your next great teammate.",
    roles: ["Admin", "RH", "Assistente"],
    employeeVisible: true,
    defaultSort: "date",
    writeRoles: ["Admin", "RH"],
    fields: [
      ...person,
      { key: "phone", label: "Phone number", type: "tel", required: true },
      department,
      {
        key: "recruiter",
        label: "Recruiter",
        type: "rhUser",
        required: true,
      },
      { key: "date", label: "Interview date", type: "date", required: true },
      { key: "time", label: "Time", type: "time", required: true },
      {
        key: "location",
        label: "Location",
        type: "select",
        options: ["1er Etage", "2eme Etage", "3eme Etage", "Mezanin"],
        required: true,
      },
      {
        key: "hrManager",
        label: "HR manager",
        type: "rhUser",
        required: true,
      },
      {
        key: "technicalManager",
        label: "Technical manager",
        type: "employeeName",
        required: true,
      },
    ],
    columns: [
      {
        key: "firstName",
        label: "First name",
      },
      {
        key: "lastName",
        label: "Last name",
      },
      {
        key: "phone",
        label: "Phone number",
      },
      {
        key: "departmentId",
        label: "Department",
      },
      {
        key: "recruiter",
        label: "Recruiter",
      },
      {
        key: "date",
        label: "Date",
      },
      {
        key: "time",
        label: "Time",
      },
      {
        key: "hrManager",
        label: "HR manager",
      },
      {
        key: "technicalManager",
        label: "Technical manager",
      },
      {
        key: "location",
        label: "Location",
      },
      {
        key: "interviewStatus",
        label: "Status",
      },
    ],
  },
  {
    key: "meeting-reservations",
    title: "Meeting room reservations",
    singular: "meeting room reservation",
    description: "Reserve an available meeting room for your team.",
    roles: ["Admin", "RH", "Assistente", "Direction", "User"],
    employeeVisible: true,
    writeRoles: ["*"],
    fields: [
      { key: "title", label: "Meeting title", required: true, wide: true },
      { key: "date", label: "Meeting date", type: "date", required: true },
      { key: "floorId", label: "Floor", type: "floor", required: true },
      { key: "roomId", label: "Meeting room", type: "meetingRoom", required: true },
      { key: "startTime", label: "Start time", type: "time", required: true },
      { key: "endTime", label: "End time", type: "time", required: true },
      { key: "participantIds", label: "Participants", type: "participants", wide: true },
    ],
    columns: [
      { key: "title", label: "Meeting title" },
      { key: "date", label: "Date" },
      { key: "startTime", label: "Start time" },
      { key: "endTime", label: "End time" },
      { key: "floorId", label: "Floor" },
      { key: "roomId", label: "Meeting room" },
      { key: "reservedBy", label: "Reserved by" },
      { key: "status", label: "Status" },
    ],
  },
  {
    key: "employees",
    title: "Employees",
    singular: "employee",
    description: "Manage employees and their application access.",
    roles: ["Admin"],
    organization: true,
    fields: [
      { key: "fullName", label: "Full name", required: true },
      { key: "email", label: "Email address", type: "email", required: true },
      department,
      { key: "managedDepartmentIds", label: "Managed departments", type: "managedDepartments" },
      {
        key: "role",
        label: "Role",
        type: "role",
        required: true,
      },
      { key: "active", label: "Employee active", type: "checkbox" },
    ],
    columns: [
      { key: "fullName", label: "Employee" },
      { key: "email", label: "Email" },
      { key: "departmentId", label: "Department" },
      { key: "managedDepartmentIds", label: "Managed departments" },
      { key: "role", label: "Role" },
      { key: "active", label: "Status" },
    ],
  },
  {
    key: "departments",
    title: "Departments",
    singular: "department",
    description: "Keep your organization connected and clearly structured.",
    roles: ["Admin"],
    organization: true,
    fields: [
      { key: "name", label: "Department name (English)", required: true },
      { key: "nameFr", label: "Department name (French)", required: true },
    ],
    columns: [
      { key: "name", label: "Department name (English)" },
      { key: "nameFr", label: "Department name (French)" },
      { key: "createdAt", label: "Created" },
    ],
  },
  {
    key: "floors",
    title: "Floors",
    singular: "floor",
    description: "Manage the floors available for meeting rooms.",
    roles: ["Admin"],
    organization: true,
    fields: [
      { key: "name", label: "Floor name (English)", required: true },
      { key: "nameFr", label: "Floor name (French)", required: true },
      { key: "active", label: "Floor available", type: "checkbox" },
    ],
    columns: [
      { key: "name", label: "Floor name (English)" },
      { key: "nameFr", label: "Floor name (French)" },
      { key: "active", label: "Available" },
    ],
  },
  {
    key: "roles",
    title: "Roles",
    singular: "role",
    description: "Manage the roles available for employee accounts.",
    roles: ["Admin"],
    organization: true,
    defaultSort: "name",
    fields: [
      { key: "name", label: "Role name (English)", required: true },
      { key: "nameFr", label: "Role name (French)", required: true },
    ],
    columns: [
      { key: "name", label: "Role name (English)" },
      { key: "nameFr", label: "Role name (French)" },
    ],
  },
  {
    key: "meeting-rooms",
    title: "Meeting rooms",
    singular: "meeting room",
    description: "Manage the rooms available for meetings on each floor.",
    roles: ["Admin"],
    organization: true,
    fields: [
      { key: "name", label: "Room name", required: true },
      { key: "floorId", label: "Floor", type: "floor", required: true },
      { key: "active", label: "Room available", type: "checkbox" },
    ],
    columns: [
      { key: "name", label: "Room name" },
      { key: "floorId", label: "Floor" },
      { key: "active", label: "Available" },
    ],
  },
];
export const roleLabel: Record<string, string> = {
  Admin: "Administrator",
  RH: "Human resources",
  Assistente: "Reception",
  Direction: "Management",
  User: "Member",
};
